import { useMemo, useState } from "react";
import { Alert, Anchor, Badge, Button, Group, Loader, Stack, Table, Text, TextInput, Title } from "@mantine/core";
import { IconAlertTriangle, IconRefresh, IconSearch } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import type { Locale } from "@iso27001/shared";
import { docsApi, type DocumentListItem } from "../../api/docs.js";
import { useAuth } from "../../auth/AuthContext.js";
import { formatVersionDate } from "./format-version-date.js";
import { useEnumLabel } from "../../i18n/enum-label.js";
import { useApiErrorMessage } from "../../i18n/api-error.js";

const CLASSIFICATION_COLOR: Record<DocumentListItem["classification"], string> = {
  "öffentlich": "green",
  intern: "blue",
  vertraulich: "red",
};

export function DokumentenlenkungPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [q, setQ] = useState("");
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  const { label } = useEnumLabel();
  const apiError = useApiErrorMessage();

  // Deliberately no locale: the Dokumentenlenkung register is the compliance
  // artefact and lists the German records. The public reader index localises;
  // this does not. See the English documentation design spec §7.
  const { data, isLoading, error } = useQuery({ queryKey: ["docs"], queryFn: () => docsApi.list() });
  const diagnostics = useQuery({
    queryKey: ["docs-diagnostics"],
    queryFn: docsApi.diagnostics,
    enabled: isAdmin,
  });

  const reindex = useMutation({
    mutationFn: docsApi.reindex,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["docs"] });
      qc.invalidateQueries({ queryKey: ["docs-diagnostics"] });
      notifications.show({ message: t("docs.control.notify.reindexed", { count: r.indexed }), color: "teal" });
    },
    onError: (e) => notifications.show({ message: apiError(e, { fallback: "docs.control.error.reindexFailed" }), color: "red" }),
  });

  const items = useMemo(() => {
    const all = data?.items ?? [];
    const needle = q.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(
      (d) =>
        d.title.toLowerCase().includes(needle) ||
        d.slug.toLowerCase().includes(needle) ||
        (d.sectionTitle ?? "").toLowerCase().includes(needle),
    );
  }, [data, q]);

  if (isLoading) return <Loader />;
  if (error) return <Alert color="red">{t("docs.control.loadError")}</Alert>;

  const d = diagnostics.data;
  // A bundle that failed to unpack (corrupt archive, permission error, ...)
  // is reported as stale with the cause in `error`; a bundle that simply has
  // not been built yet is stale: false, pageCount: 0 and belongs with
  // docsDirConfigured/pageCount rather than here.
  const bundleWarnings = d?.bundles.filter((b) => b.stale || b.error) ?? [];
  const hasWarnings =
    !!d &&
    (d.defaultClassified.length > 0 ||
      d.publishedMismatch.length > 0 ||
      d.publishedAboveClearance.length > 0 ||
      d.versionBehindHistory.length > 0 ||
      d.versionUndated.length > 0 ||
      d.unpairedTranslations.length > 0 ||
      d.translationClassificationMismatch.length > 0 ||
      d.missing.length > 0 ||
      bundleWarnings.length > 0 ||
      d.sourcesNewerThanBundles);

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>{t("docs.control.title")}</Title>
        {isAdmin && (
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            loading={reindex.isPending}
            onClick={() => reindex.mutate()}
          >
            {t("docs.control.reindexButton")}
          </Button>
        )}
      </Group>

      {isAdmin && d && !d.docsDirConfigured && (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
          {/*
            One key, not a .before/.after pair around the <code>. The plan
            forbids assembling a sentence from several keys because German and
            English order clauses differently; this one only appeared to work
            because both happen to put the parenthetical last. <Trans> keeps
            the sentence a single translatable unit and lets the translation
            move the tag.
          */}
          <Trans i18nKey="docs.control.noDocsDir" components={{ code: <code /> }} />
        </Alert>
      )}

      {isAdmin && hasWarnings && (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />} title={t("docs.control.diagnostics.title")}>
          <Stack gap={4}>
            {d!.defaultClassified.length > 0 && (
              <Text size="sm">{t("docs.control.diagnostics.defaultClassified", { slugs: d!.defaultClassified.join(", ") })}</Text>
            )}
            {d!.publishedMismatch.length > 0 && (
              <Text size="sm">{t("docs.control.diagnostics.publishedMismatch", { slugs: d!.publishedMismatch.join(", ") })}</Text>
            )}
            {d!.publishedAboveClearance.length > 0 && (
              <Text size="sm" fw={700} c="red">
                {t("docs.control.diagnostics.publishedAboveClearance", { slugs: d!.publishedAboveClearance.join(", ") })}
              </Text>
            )}
            {d!.versionBehindHistory.length > 0 && (
              <Text size="sm">
                {t("docs.control.diagnostics.versionBehindHistory", { slugs: d!.versionBehindHistory.join(", ") })}
              </Text>
            )}
            {d!.versionUndated.length > 0 && (
              <Text size="sm">
                {t("docs.control.diagnostics.versionUndated", { slugs: d!.versionUndated.join(", ") })}
              </Text>
            )}
            {d!.unpairedTranslations.length > 0 && (
              <Text size="sm">
                {t("docs.control.diagnostics.unpairedTranslations", { slugs: d!.unpairedTranslations.join(", ") })}
              </Text>
            )}
            {d!.translationClassificationMismatch.length > 0 && (
              <Text size="sm">
                {t("docs.control.diagnostics.translationClassificationMismatch", { slugs: d!.translationClassificationMismatch.join(", ") })}
              </Text>
            )}
            {d!.missing.length > 0 && (
              <Text size="sm">{t("docs.control.diagnostics.missing", { slugs: d!.missing.join(", ") })}</Text>
            )}
            {bundleWarnings.length > 0 && (
              <Text size="sm">
                {t("docs.control.diagnostics.bundleWarnings", {
                  slugs: bundleWarnings.map((b) => (b.error ? `${b.slug} (${b.error})` : b.slug)).join(", "),
                })}
              </Text>
            )}
            {d!.sourcesNewerThanBundles && (
              <Text size="sm">{t("docs.control.diagnostics.sourcesNewerThanBundles")}</Text>
            )}
          </Stack>
        </Alert>
      )}

      <TextInput
        placeholder={t("docs.control.searchPlaceholder")}
        leftSection={<IconSearch size={16} />}
        value={q}
        onChange={(e) => setQ(e.currentTarget.value)}
      />

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("docs.field.title")}</Table.Th>
            <Table.Th>{t("docs.field.section")}</Table.Th>
            <Table.Th>{t("docs.field.version")}</Table.Th>
            <Table.Th>{t("docs.field.asOf")}</Table.Th>
            <Table.Th>{t("docs.field.author")}</Table.Th>
            <Table.Th>{t("docs.field.approval")}</Table.Th>
            <Table.Th>{t("docs.field.classification")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((doc) => (
            <Table.Tr key={doc.id}>
              <Table.Td>
                {/*
                  Every row here is already readable by this caller: the list
                  endpoint filters to present = true and the caller's clearance,
                  so a link can never lead to a 404 or a denial.
                */}
                <Anchor component={Link} to={`/dokumentation/${doc.slug}`}>
                  {doc.title}
                </Anchor>
              </Table.Td>
              <Table.Td>{doc.sectionTitle ?? "—"}</Table.Td>
              <Table.Td>{doc.version ?? "—"}</Table.Td>
              <Table.Td>{formatVersionDate(doc.versionDate, locale) ?? "—"}</Table.Td>
              <Table.Td>{doc.ersteller ?? "—"}</Table.Td>
              <Table.Td>{doc.freigabe ?? "—"}</Table.Td>
              <Table.Td>
                <Badge color={CLASSIFICATION_COLOR[doc.classification]} variant="light">
                  {label("clearance", doc.classification)}
                </Badge>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {items.length === 0 && <Text c="dimmed">{t("docs.control.empty")}</Text>}
    </Stack>
  );
}
